import { Component, OnInit } from '@angular/core';
import { CoreComponent } from '../core.component';
import { RoofProducerEnum } from '../../api/models/roof-producer-enum';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ProductsService } from '../../api/services/products.service';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom, Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { ProductThicknessListDto } from '../../api/models/product-thickness-list-dto';
import { ProductFinishesListDto } from '../../api/models/product-finishes-list-dto';
import { ProductColorListDto } from '../../api/models/product-color-list-dto';
import { setModels } from '../calculation/calculation.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { FeaturedProductsModal } from '../featured-products/featured-products-modal/featured-products-modal';
import { CalculationModelsState } from '../calculation/calculation.state';
import { MatDialog } from '@angular/material/dialog';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Title } from '@angular/platform-browser';
import { CalculationStepRoutes } from '../calculation/calculation.constants';
import { ProductModel } from './product.model';

@Component({
  selector: 'tile-product',
  templateUrl: './tile-product.component.html',
  styleUrls: ['./tile-product.component.scss'],
})
export class TileProductComponent extends CoreComponent implements OnInit {
  imagePath = '';
  colorWarrantyInYears = 0;
  corrosionWarrantyInYears = 0;
  lifetimeInYears = 0;
  form = this.formBuilder.group({
    finish: new UntypedFormControl('', [Validators.required]),
    thickness: new UntypedFormControl('', [Validators.required]),
    color: new UntypedFormControl('', [Validators.required]),
    hasDripStop: new UntypedFormControl(false),
  });
  product!: ProductModel;
  finishes!: Observable<ProductFinishesListDto>;
  thicknesses!: Observable<ProductThicknessListDto>;
  colors!: Observable<ProductColorListDto>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly productsService: ProductsService,
    private breadcrumbService: BreadcrumbService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private title: Title,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    const productId = this.route.snapshot.paramMap.get('id');
    const name = this.route.snapshot.paramMap.get('name');
    if (!productId) {
      this.router.navigate(['404']);
      return;
    }
    try {
      this.product = new ProductModel(
        await lastValueFrom(this.productsService.productsControllerGetProduct({ productId })),
      );
    } catch (exception) {
      this.router.navigate(['404']);
      return;
    }
    this.imagePath = this.product.imageUrl ?? '';
    if (this.product.isTile) {
      this.title.setTitle(`Țiglă metalică ${this.product.producer} ${this.product.name}`);
      this.breadcrumbService.set(
        `produse/tigla-metalica/${name}/${productId}`,
        `Țiglă metalică ${this.product.producer} ${this.product.name}`,
      );
    } else {
      this.title.setTitle(`${this.product.producer} ${this.product.name}`);
      this.breadcrumbService.set(
        `produse/accesoriu/${name}/${productId}`,
        `${this.product.producer} ${this.product.name}`,
      );
    }
    this.updateCanonicalLink(`produse/tigla-metalica/${name}/${productId}`);
    this.modelChange();
  }

  getImagePathFromForm(): string {
    return this.product.imageUrl ?? '';
  }

  updateWarranty(): void {
    const finish = this.form.controls.finish?.value?.finish?.toLowerCase();
    const thickness = this.form.controls.thickness?.value?.thickness;
    if (thickness < 0.4) {
      this.corrosionWarrantyInYears = this.colorWarrantyInYears = 2;
    } else if (thickness === 0.4) {
      this.corrosionWarrantyInYears = this.colorWarrantyInYears = 5;
    } else if (finish === 'lucios' || finish === 'lucios 8003') {
      this.corrosionWarrantyInYears = this.colorWarrantyInYears = 10;
    } else if (this.product?.producer === RoofProducerEnum.Bilka) {
      if (finish === 'mat' || finish === 'wood') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 15;
      } else if (finish === 'grande mat') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 30;
      } else {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 0;
      }
    } else if (this.product?.producer === RoofProducerEnum.Wetterbest) {
      if (finish === 'mat' || finish === 'wood') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 15;
      } else if (finish === 'neomat 30') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 30;
      } else if (finish === 'suprem 50') {
        this.corrosionWarrantyInYears = 50;
        this.colorWarrantyInYears = 25;
      } else {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 0;
      }
    }
    this.lifetimeInYears = this.product?.producer === RoofProducerEnum.Wetterbest ? 50 : 60;
  }

  getDefaultFinishColor(finish: string) {
    return finish === 'lucios 8003' ? '8003' : '8017';
  }

  getFullPrice(): number {
    let price = this.form.controls.thickness.value.price;
    if (this.form.controls.hasDripStop.value) {
      price += this.form.controls.thickness.value.dripStopPrice;
    }

    return price;
  }

  async modelChange() {
    this.finishes = this.productsService
      .productsControllerGetRoofFinishes({ productId: this.product.id, limit: 1000, offset: 0 })
      .pipe(
        shareReplay(),
        tap((value) => {
          const selectedValue = this.getSelectedValue(
            value.results,
            'finish',
            (finish) => finish.finish.toLowerCase() === 'lucios',
          );
          this.form.controls.finish.patchValue(selectedValue);
          this.finishChange();
        }),
      );
  }

  async finishChange() {
    this.thicknesses = this.productsService
      .productsControllerGetRoofThicknesses({
        productId: this.product.id,
        finishId: this.form.controls.finish.value.id,
      })
      .pipe(
        shareReplay(),
        tap((value) => {
          const selectedValue = this.getSelectedValue(value.results, 'thickness');
          this.form.controls.thickness.patchValue(selectedValue);
          this.thicknessChange();
          return value.results;
        }),
      );
  }

  async thicknessChange() {
    this.colors = this.productsService
      .productsControllerGetRoofColors({
        productId: this.product.id,
        thicknessId: this.form.controls.thickness.value.id,
        finishId: this.form.controls.finish.value.id,
        limit: 1000,
        offset: 0,
      })
      .pipe(
        shareReplay(),
        tap((value) => {
          const selectedValue = this.getSelectedValue(value.results, 'color');
          this.form.controls.color.patchValue(selectedValue);
          this.colorChange();
        }),
      );
    this.updateWarranty();
  }

  async colorChange() {
    if (this.product.isTile) {
      this.imagePath = this.getImagePathFromForm();
    }
    this.meta.updateTag({ content: this.imagePath, name: 'og:image' });
    this.meta.updateTag({ content: this.imagePath, name: 'twitter:image' });
  }

  private getSelectedValue(results: any[], formControlName: string, customFindFunction?: (value: any) => any) {
    const controlValue = this.form.controls[formControlName].value;
    let value;
    if (controlValue) {
      value = results.find((result: any) => result.id === controlValue.id);
    }
    if (!value && customFindFunction) {
      value = results.find(customFindFunction);
    }

    return value ? value : results[0];
  }

  openDialog() {
    const dialogRef = this.dialog.open(FeaturedProductsModal, {
      data: { route: CalculationStepRoutes.MEASUREMENTS },
      minWidth: '360px',
      width: '500px',
    });
    dialogRef.beforeClosed().subscribe(() => {
      const models: CalculationModelsState = {
        producer: this.product.producer,
        roof: this.product.getProductDto(),
        finish: this.form.controls.finish.value,
        thickness: this.form.controls.thickness.value,
        color: this.form.controls.color.value,
        hasDripStop: false,
      };
      this.store.dispatch(setModels({ models }));
    });
  }
}
